import { useEffect, useState, useCallback, useMemo } from 'react'
import axios from 'axios'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin

/**
 * Super-admin Margin Inspector — trace where a booking's price came from.
 *
 * Answers reseller/support price questions ("the room is $185 on Expedia, why
 * is the package $494/person?") by decomposing the customer's gross into every
 * dollar: raw vendor cost (hotel + golf), the markup InteGolf added, platform +
 * gateway fees, tax, and — usually the biggest slice — the RESELLER's own net
 * on the price THEY set. The reconciling waterfall makes it self-evident that
 * the gap over Expedia isn't an InteGolf fee, it's the seller's margin.
 *
 * Search by booking #, golfer, reseller, or package. Numbers are exact
 * ("settled") once a component is confirmed with the provider, otherwise an
 * estimate backed out of the stored storefront price (flagged).
 */
const money = (n, cur = 'CAD') =>
  (n == null ? '—' : `${cur} ${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
const pct = (n) => `${(Number(n) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
const fmtDate = v => { if (!v) return '—'; const d = new Date(String(v).slice(0, 10) + 'T00:00:00'); return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
const fmtTime = v => { if (!v) return ''; const m = String(v).match(/(\d{1,2}):(\d{2})/); if (!m) return String(v); let h = Number(m[1]); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${m[2]} ${ap}` }

export default function MarginInspector() {
  return (
    <AdminShell title="Margin Inspector">
      <MarginInspectorBody base="/admin/resellers/margin-inspector" />
    </AdminShell>
  )
}

/**
 * The tool body, parameterized by API base path so it can be embedded in the
 * super-admin shell (/admin/resellers/margin-inspector) or the reseller portal
 * (/portal/reseller/margin-inspector, scoped to their own account).
 */
export function MarginInspectorBody({ base }) {
  const [mode, setMode] = useState('lookup')
  return (
    <>
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid #e2e8f0' }}>
        <TabBtn active={mode === 'lookup'} onClick={() => setMode('lookup')}>Look up a booking</TabBtn>
        <TabBtn active={mode === 'simulate'} onClick={() => setMode('simulate')}>Simulate a package</TabBtn>
      </div>
      {mode === 'lookup' ? <LookupTab base={base} /> : <SimulateTab base={base} />}
    </>
  )
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 16px', fontSize: 13.5, fontWeight: 600, border: 'none', cursor: 'pointer',
      background: 'transparent', color: active ? '#0f172a' : '#94a3b8',
      borderBottom: active ? '2px solid #0f172a' : '2px solid transparent', marginBottom: -1,
    }}>{children}</button>
  )
}

function ModeBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '8px 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
      border: '1px solid ' + (active ? '#0f172a' : '#cbd5e1'), background: active ? '#0f172a' : '#fff', color: active ? '#fff' : '#475569',
    }}>{children}</button>
  )
}

// Build-your-own price build-up: cost -> + reseller markup -> + platform fee ->
// + tax = customer total; gateway deducted from the reseller's markup.
function ByoBuildUp({ w, cur, g, paymentMode }) {
  const taxLabel = `Tax / HST (${(w.taxRate * 100).toFixed(w.taxRate * 100 % 1 ? 1 : 0)}%)`
  const mkLabel = `Reseller markup (${(w.resellerMarkupPct * 100).toFixed(w.resellerMarkupPct * 100 % 1 ? 1 : 0)}%)`
  const rows = [
    { label: 'Hotel + golf cost (incl. our markup)', amt: w.costBase, tone: '#334155', first: true },
    { label: mkLabel, amt: w.resellerMarkup, tone: '#7c3aed', running: w.afterResellerMarkup },
    { label: `InteGolf platform fee (${pct(w.platformPct)})`, amt: w.platformFee, tone: '#0d9488', running: w.preTax },
    { label: taxLabel, amt: w.tax, tone: '#b45309', running: w.total },
  ]
  return (
    <>
      <SectionTitle>Price build-up</SectionTitle>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '6px 0', color: '#334155' }}>{r.first ? '' : '+ '}{r.label}</td>
              <td style={{ padding: '6px 0', textAlign: 'right', color: r.tone, fontWeight: 600, whiteSpace: 'nowrap' }}>{money(r.amt, cur)}</td>
              <td style={{ padding: '6px 0', textAlign: 'right', color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>{r.running != null ? `→ ${money(r.running, cur)}` : ''}</td>
            </tr>
          ))}
          <tr><td colSpan={3} style={{ borderTop: '2px solid #0f172a', padding: 0 }} /></tr>
          <tr>
            <td style={{ padding: '6px 0', fontWeight: 800, color: '#0f172a' }}>Customer pays</td>
            <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>{money(w.total, cur)}</td>
            <td style={{ padding: '6px 0', textAlign: 'right', color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>{money(round2(w.total / (g || 1)), cur)}/golfer</td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: 10, padding: '10px 12px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, fontSize: 12.5, color: '#6b21a8' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Reseller markup</span><b>{money(w.resellerMarkup, cur)}</b>
        </div>
        {w.gatewayFee > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0d9488' }}>
            <span>− Gateway {pct(w.gatewayPct)} (Mode A, from the total)</span><b>−{money(w.gatewayFee, cur)}</b>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e9d5ff', marginTop: 4, paddingTop: 4, fontWeight: 800 }}>
          <span>Reseller net{paymentMode === 'B' ? ' (before own processor fee)' : ''}</span><b>{money(w.resellerNet, cur)}</b>
        </div>
      </div>
    </>
  )
}

function LookupTab({ base }) {
  const [q, setQ] = useState('')
  const [list, setList] = useState(null)
  const [listErr, setListErr] = useState(null)
  const [listLoading, setListLoading] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 25

  const [selId, setSelId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailErr, setDetailErr] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const search = useCallback(() => {
    setListLoading(true)
    tournamentApi.get(`${base}/bookings`, { params: { q: q || undefined, page, pageSize } })
      .then(r => { setList(r.data); setListErr(null) })
      .catch(e => setListErr(e?.response?.data?.message || e.message))
      .finally(() => setListLoading(false))
  }, [q, page, base])
  useEffect(() => { search() }, [search])

  const openBooking = useCallback((id) => {
    setSelId(id); setDetail(null); setDetailErr(null); setDetailLoading(true)
    tournamentApi.get(`${base}/bookings/${id}`)
      .then(r => setDetail(r.data))
      .catch(e => setDetailErr(e?.response?.data?.message || e.message))
      .finally(() => setDetailLoading(false))
  }, [base])

  const rows = list?.rows || []
  const total = list?.total || 0
  const pages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px', maxWidth: 820, lineHeight: 1.55 }}>
        Trace where a booking's price came from. Every dollar of the customer total is broken out into raw vendor
        cost (hotel + golf), the markup InteGolf added, platform + gateway fees, tax, and the reseller's own net on
        the price they set. Use it to reconcile price questions from resellers or customers.
      </p>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Results list */}
        <div style={{ flex: '1 1 360px', minWidth: 320, maxWidth: 520 }}>
          <form onSubmit={e => { e.preventDefault(); setPage(1); search() }} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Booking #, golfer, reseller, or package…"
              style={{ flex: 1, padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
            />
            <button type="submit" style={primaryBtn}>Search</button>
          </form>

          {listErr && <ErrBox>{listErr}</ErrBox>}

          {listLoading && list === null ? <div style={{ color: '#64748b' }}>Loading…</div>
            : rows.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc', color: '#64748b', fontSize: 13 }}>
                No bookings match.
              </div>
            ) : (
              <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' }}>
                {rows.map(b => (
                  <button key={b.id} onClick={() => openBooking(b.id)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                      borderTop: '1px solid #f1f5f9', padding: '10px 13px',
                      background: selId === b.id ? '#eef6ff' : '#fff',
                      borderLeft: selId === b.id ? '3px solid #2563eb' : '3px solid transparent',
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 13.5 }}>#{b.id} · {b.customer || '—'}</span>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{money(b.total, b.currency)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {b.reseller} · {b.package || 'Tee time'} · {fmtDate(b.checkIn)} · {b.numGolfers}g
                    </div>
                  </button>
                ))}
              </div>
            )}

          {total > pageSize && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={pgBtn(page <= 1)}>← Prev</button>
              <span style={{ fontSize: 12.5, color: '#64748b' }}>Page {page} of {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))} style={pgBtn(page >= pages)}>Next →</button>
            </div>
          )}
        </div>

        {/* Detail */}
        <div style={{ flex: '2 1 460px', minWidth: 340 }}>
          {!selId ? (
            <div style={{ padding: 40, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc', color: '#64748b', fontSize: 13 }}>
              Select a booking to see its full price breakdown.
            </div>
          ) : detailLoading ? <div style={{ color: '#64748b' }}>Loading breakdown…</div>
            : detailErr ? <ErrBox>{detailErr}</ErrBox>
            : detail ? <Breakdown detail={detail} /> : null}
        </div>
      </div>
    </>
  )
}

function Breakdown({ detail }) {
  const { booking: bk, waterfall: w, perGolfer: pg, rounds } = detail
  const cur = bk.currency
  const g = bk.numGolfers || 1

  // Who-keeps-what summary.
  const vendor = round2(w.hotel.raw + w.golf.raw)
  const integolf = w.integolfKeeps

  return (
    <div style={{ border: '1px solid #e5eaf1', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      {/* Header */}
      <div style={{ padding: '16px 18px', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>#{bk.id} · {bk.customer}</div>
            <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 3 }}>
              {bk.reseller} · {bk.package || 'Tee time'}{bk.grouping ? ` · ${bk.grouping}` : ''}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              {fmtDate(bk.checkIn)} → {fmtDate(bk.checkOut)} · {g} golfer{g > 1 ? 's' : ''} · {bk.numRooms} room{bk.numRooms > 1 ? 's' : ''} · Mode {bk.paymentMode}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Customer paid</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{money(w.total, cur)}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{money(pg.total, cur)}/golfer</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <Badge tone={bk.status === 'confirmed' ? 'green' : 'slate'}>{bk.status}</Badge>
          {detail.estimated && <Badge tone="amber">estimated — not all components confirmed</Badge>}
          {w.capApplied && <Badge tone="blue">margin cap applied</Badge>}
          {bk.isManagedChild && <Badge tone="slate">managed reseller</Badge>}
        </div>
      </div>

      {/* Waterfall */}
      <div style={{ padding: '14px 18px' }}>
        {detail.pricingMode === 'byo' ? <ByoBuildUp w={w} cur={cur} g={g} paymentMode={bk.paymentMode} /> : (
        <>
        <SectionTitle>Where the money goes</SectionTitle>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead>
            <tr style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 700 }}>Line</th>
              <th style={{ textAlign: 'right', padding: '4px 0', fontWeight: 700 }}>Amount</th>
              <th style={{ textAlign: 'right', padding: '4px 0', fontWeight: 700 }}>/golfer</th>
            </tr>
          </thead>
          <tbody>
            <Line label="Hotel — paid to Expedia (raw rate)" hint={hotelHint(w.hotel)} amt={w.hotel.raw} per={perG(w.hotel.raw, g)} cur={cur} tone="vendor" />
            {w.hotel.netNightly != null && (
              <tr>
                <td colSpan={3} style={{ padding: '0 0 4px 16px' }}>
                  <span style={{ fontSize: 11.5, color: '#64748b' }}>
                    Net paid to Expedia <b>{money(w.hotel.netNightly, cur)}/night</b>
                    {' · '}with markup {money(w.hotel.markedNightly, cur)}/night
                    {w.hotel.rooms > 1 || w.hotel.nights > 1 ? ` (${w.hotel.rooms} room${w.hotel.rooms > 1 ? 's' : ''} × ${w.hotel.nights} night${w.hotel.nights > 1 ? 's' : ''})` : ''}
                    {' — '}<span style={{ color: '#94a3b8' }}>compare to a public expedia.com rate to check parity</span>
                  </span>
                </td>
              </tr>
            )}
            <Line label={`Hotel markup`} amt={w.hotel.markup} per={perG(w.hotel.markup, g)} cur={cur} tone="markup" indent />
            <Line label="Golf — paid to course (raw green fee)" hint={golfHint(w.golf)} amt={w.golf.raw} per={perG(w.golf.raw, g)} cur={cur} tone="vendor" />
            <Line label="Golf markup" amt={w.golf.markup} per={perG(w.golf.markup, g)} cur={cur} tone="markup" indent />
            <Line label={`InteGolf platform fee (${pct(w.platformPct)})`} amt={w.platformFee} per={perG(w.platformFee, g)} cur={cur} tone="fee" />
            {w.gatewayFee > 0 && <Line label={`Gateway fee (${pct(w.gatewayPct)})`} amt={w.gatewayFee} per={perG(w.gatewayFee, g)} cur={cur} tone="fee" />}
            {w.parentMarkup > 0 && <Line label="Parent reseller markup" amt={w.parentMarkup} per={perG(w.parentMarkup, g)} cur={cur} tone="markup" />}
            <Line label={`Tax / HST (${(w.taxRate * 100).toFixed(w.taxRate * 100 % 1 ? 1 : 0)}%, remitted)`} amt={w.tax} per={perG(w.tax, g)} cur={cur} tone="tax" />
            <tr><td colSpan={3} style={{ borderTop: '2px solid #0f172a', padding: 0 }} /></tr>
            <Line label="Reseller net (their margin on this sale)" amt={w.resellerNet} per={perG(w.resellerNet, g)} cur={cur} tone="net" bold />
          </tbody>
        </table>
        </>
        )}

        {w.capApplied && (
          <div style={{ marginTop: 10, padding: '9px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12.5, color: '#1e40af' }}>
            <b>Margin cap applied.</b> The customer was discounted {money(w.capDiscount, cur)} so the reseller's net was held to the package's
            maximum-revenue cap{bk.maxRevenueCap != null ? ` of ${money(bk.maxRevenueCap, cur)}` : ''}. Without the cap the reseller net would have been {money(round2(w.resellerNet + w.capDiscount), cur)}.
          </div>
        )}

        {/* Who keeps what */}
        <SectionTitle style={{ marginTop: 18 }}>Who keeps what</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
          <Keeper label="Vendors (Expedia + course)" amt={vendor} cur={cur} tone="#475569" note="raw cost" />
          <Keeper label="InteGolf" amt={integolf} cur={cur} tone="#0d9488" note="markup + fees" />
          <Keeper label="Reseller" amt={w.resellerNet} cur={cur} tone="#7c3aed" note="net margin" />
          <Keeper label="Tax" amt={w.tax} cur={cur} tone="#b45309" note="remitted" />
        </div>

        {/* Rounds detail */}
        {rounds.length > 0 && (
          <>
            <SectionTitle style={{ marginTop: 18 }}>Rounds</SectionTitle>
            <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden' }}>
              {rounds.map(r => (
                <div key={r.roundIndex} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '9px 12px', borderTop: r.roundIndex ? '1px solid #f1f5f9' : 'none', alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.courseName}</div>
                    <div style={{ fontSize: 11.5, color: '#64748b' }}>{fmtDate(r.playDate)} {fmtTime(r.teeTime)} · <SourceTag source={r.source} /></div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12.5 }}>
                    {r.source === 'none'
                      ? <span style={{ color: '#b45309' }}>cost unknown</span>
                      : <span style={{ color: '#475569' }}>raw {money(r.raw, cur)} + markup {money(r.markup, cur)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {detail.estimated && (
          <div style={{ marginTop: 14, padding: '9px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
            Some components aren't confirmed with the provider yet, so their raw cost is <b>estimated</b> by backing the reseller's current
            markup out of the stored price. Once confirmed, the numbers become exact (the real amount charged by Expedia / the course).
          </div>
        )}
      </div>
    </div>
  )
}

function hotelHint(h) {
  if (h.source === 'none') return null
  return h.source === 'estimated' ? 'estimated' : null
}
function golfHint(gf) {
  return gf.hasEstimate && !gf.hasSettled ? 'estimated' : null
}

function Line({ label, hint, amt, per, cur, tone, indent, bold }) {
  const color = {
    vendor: '#334155', markup: '#0d9488', fee: '#0d9488', tax: '#b45309', net: '#7c3aed',
  }[tone] || '#334155'
  return (
    <tr>
      <td style={{ padding: '6px 0', paddingLeft: indent ? 16 : 0, color: bold ? color : '#334155', fontWeight: bold ? 700 : 400 }}>
        {label}
        {hint && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>({hint})</span>}
      </td>
      <td style={{ padding: '6px 0', textAlign: 'right', color, fontWeight: bold ? 800 : 600, whiteSpace: 'nowrap' }}>{money(amt, cur)}</td>
      <td style={{ padding: '6px 0', textAlign: 'right', color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>{money(per, cur)}</td>
    </tr>
  )
}

function Keeper({ label, amt, cur, tone, note }) {
  return (
    <div style={{ border: `1px solid ${tone}22`, background: `${tone}0d`, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 11.5, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: tone, marginTop: 2 }}>{money(amt, cur)}</div>
      <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{note}</div>
    </div>
  )
}

function SourceTag({ source }) {
  if (source === 'settled') return <span style={{ color: '#166534', fontWeight: 600 }}>settled</span>
  if (source === 'estimated') return <span style={{ color: '#92400e', fontWeight: 600 }}>estimated</span>
  return <span style={{ color: '#94a3b8' }}>no cost</span>
}

function Badge({ children, tone }) {
  const map = {
    green: { bg: '#dcfce7', c: '#166534' }, amber: { bg: '#fef3c7', c: '#92400e' },
    blue: { bg: '#dbeafe', c: '#1e40af' }, slate: { bg: '#e2e8f0', c: '#475569' },
  }
  const t = map[tone] || map.slate
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: t.bg, color: t.c }}>{children}</span>
}

function SectionTitle({ children, style }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase', margin: '0 0 8px', ...style }}>{children}</div>
}
function ErrBox({ children }) {
  return <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{children}</div>
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100
const perG = (n, g) => (g > 0 ? round2(n / g) : n)
const primaryBtn = { padding: '9px 16px', fontSize: 13, fontWeight: 600, border: 'none', background: '#0f172a', color: '#fff', borderRadius: 8, cursor: 'pointer' }
const pgBtn = (disabled) => ({ padding: '7px 14px', fontSize: 13, border: '1px solid #cbd5e1', background: disabled ? '#f1f5f9' : '#fff', color: disabled ? '#94a3b8' : '#0f172a', borderRadius: 6, cursor: disabled ? 'default' : 'pointer' })
const fieldLbl = { fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3, fontWeight: 600 }
const field = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' }

// ─── Simulate a package (guided live pick — "what would Brad's selection cost?") ─
function SimulateTab({ base }) {
  const [pq, setPq] = useState('')
  const [plist, setPlist] = useState(null)
  const [ploading, setPloading] = useState(false)
  const [sel, setSel] = useState(null)         // chosen package row
  const [detail, setDetail] = useState(null)   // public package detail (courses, defaults)
  const [detailErr, setDetailErr] = useState(null)

  const [checkIn, setCheckIn] = useState('')
  const [nights, setNights] = useState(2)
  const [golfers, setGolfers] = useState(2)
  const [rooms, setRooms] = useState(1)

  // Pricing model: fixed package (price - costs) vs build-your-own (cost + reseller markup + fees).
  const [pricingMode, setPricingMode] = useState('package') // 'package' | 'byo'
  const [resellerMarkupPct, setResellerMarkupPct] = useState(10) // whole % for byo
  const [paymentMode, setPaymentMode] = useState('A') // 'A' | 'B' (byo gateway)

  const [hotelData, setHotelData] = useState(null)   // { hotels, upgrades }
  const [hotelLoading, setHotelLoading] = useState(false)
  const [hotelErr, setHotelErr] = useState(null)
  const [hotelSel, setHotelSel] = useState(null)     // { rateKey, costToken, name, roomType }

  const [rounds, setRounds] = useState([])           // [{ coursesId, courseName, playDate, teeTime, ref }]

  const [result, setResult] = useState(null)
  const [simLoading, setSimLoading] = useState(false)
  const [simErr, setSimErr] = useState(null)

  const embed = useMemo(() => sel ? axios.create({
    baseURL: `${apiOrigin}/api/public/reseller-embed/${sel.iframeKey}`, validateStatus: () => true,
  }) : null, [sel])

  const checkOut = useMemo(() => {
    if (!checkIn) return ''
    const d = new Date(checkIn + 'T00:00:00'); d.setDate(d.getDate() + (Number(nights) || 1))
    return d.toISOString().slice(0, 10)
  }, [checkIn, nights])

  const searchPkgs = useCallback(() => {
    setPloading(true)
    tournamentApi.get(`${base}/packages`, { params: { q: pq || undefined, pageSize: 40 } })
      .then(r => setPlist(r.data)).catch(() => setPlist({ rows: [] })).finally(() => setPloading(false))
  }, [pq, base])
  useEffect(() => { searchPkgs() }, [searchPkgs])

  function pickPackage(p) {
    setSel(p); setDetail(null); setDetailErr(null); setHotelData(null); setHotelSel(null)
    setRounds([]); setResult(null); setSimErr(null); setCheckIn('')
    const e = axios.create({ baseURL: `${apiOrigin}/api/public/reseller-embed/${p.iframeKey}`, validateStatus: () => true })
    e.get(`/packages/${p.slug}`).then(r => {
      if (r.status >= 400) { setDetailErr(r.data?.message || `Could not load this package — live simulation needs an active, published package (status ${r.status}).`); return }
      const pkg = r.data.package || {}
      setDetail(r.data)
      setGolfers(pkg.numGolfers || 2)
      setNights(pkg.nights || 2)
      setRooms(pkg.roomCount || 1)
      const n = Math.max(1, Number(pkg.roundsPerGolfer) || 1)
      setRounds(Array.from({ length: n }, () => ({ coursesId: '', courseName: '', playDate: '', teeTime: '', ref: '' })))
    }).catch(err => setDetailErr(err.message))
  }

  function findHotels() {
    if (!embed || !checkIn) return
    setHotelLoading(true); setHotelErr(null); setHotelSel(null); setResult(null)
    const params = new URLSearchParams({ checkin: checkIn, nights: String(nights || 2), minStars: '0' })
    if (sel.maxHotelPerNight) params.set('budget', String(sel.maxHotelPerNight))
    if (sel.packageId) params.set('packageId', String(sel.packageId))
    embed.get('/hotels?' + params.toString()).then(r => {
      if (r.status >= 400) setHotelErr(r.data?.message || `Error ${r.status}`)
      else setHotelData({ hotels: r.data.hotels || [], upgrades: r.data.upgrades || [] })
    }).catch(e => setHotelErr(e.message)).finally(() => setHotelLoading(false))
  }

  function patchRound(idx, patch) {
    setRounds(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r)); setResult(null)
  }

  function runSim() {
    if (!embed) return
    setSimLoading(true); setSimErr(null); setResult(null)
    tournamentApi.post(`${base}/simulate`, {
      iframeKey: sel.iframeKey, packageSlug: sel.slug,
      checkInDate: checkIn, checkOutDate: checkOut,
      numGolfers: Number(golfers), numRooms: Number(rooms),
      pricingMode,
      resellerMarkupPct: pricingMode === 'byo' ? (Number(resellerMarkupPct) || 0) / 100 : undefined,
      paymentMode,
      hotel: hotelSel ? { rateKey: hotelSel.rateKey, costToken: hotelSel.costToken, name: hotelSel.name } : null,
      rounds: rounds.filter(r => r.coursesId && r.teeTime).map(r => ({
        coursesId: Number(r.coursesId), courseName: r.courseName,
        playDate: r.playDate || checkIn, teeTime: r.teeTime, ref: r.ref || null,
      })),
    }).then(r => setResult(r.data))
      .catch(e => setSimErr(e?.response?.data?.message || e.message))
      .finally(() => setSimLoading(false))
  }

  const courses = detail?.courses || []
  const roundsReady = rounds.length > 0 && rounds.every(r => r.coursesId && r.teeTime)
  const canRun = !!sel && !!checkIn && !!hotelSel && roundsReady

  // ── Package not yet chosen: show the picker ──
  if (!sel) {
    const rows = plist?.rows || []
    return (
      <div style={{ maxWidth: 640 }}>
        <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 14px', lineHeight: 1.55 }}>
          Pick a reseller package (including ones with no bookings yet), then reproduce the exact hotel + tee-time a
          customer would choose. Costs are re-derived <b>live</b> from Expedia and the courses, so you see today's margin.
        </p>
        <form onSubmit={e => { e.preventDefault(); searchPkgs() }} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={pq} onChange={e => setPq(e.target.value)} placeholder="Search package, reseller, or grouping…" style={{ ...field, flex: 1 }} />
          <button type="submit" style={primaryBtn}>Search</button>
        </form>
        {ploading && plist === null ? <div style={{ color: '#64748b' }}>Loading…</div>
          : rows.length === 0 ? <div style={{ padding: 24, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, color: '#64748b', fontSize: 13 }}>No packages match.</div>
          : (
            <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' }}>
              {rows.map(p => (
                <button key={p.packageId} onClick={() => pickPackage(p)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', borderTop: '1px solid #f1f5f9', padding: '10px 13px', background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 13.5 }}>{p.name}</span>
                    <span style={{ fontSize: 12.5, color: '#475569' }}>{p.price != null ? money(p.price, p.currency) : 'no price set'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {p.reseller} · {p.groupingName} · {p.bookingCount} booking{p.bookingCount === 1 ? '' : 's'}
                    {p.maxRevenueCap != null ? ` · cap ${money(p.maxRevenueCap, p.currency)}` : ''}
                    {p.status !== 'active' ? ` · ${p.status}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
      </div>
    )
  }

  // ── Package chosen: guided selection ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{sel.name}</div>
          <div style={{ fontSize: 12.5, color: '#64748b' }}>
            {sel.reseller} · package price {sel.price != null ? money(sel.price, sel.currency) : '—'}
            {sel.maxHotelPerNight != null ? ` · hotel budget ${money(sel.maxHotelPerNight, sel.currency)}/night` : ''}
            {sel.maxRevenueCap != null ? ` · max revenue ${money(sel.maxRevenueCap, sel.currency)}` : ''}
          </div>
        </div>
        <button onClick={() => setSel(null)} style={pgBtn(false)}>← Change package</button>
      </div>

      {detailErr && <ErrBox>{detailErr}</ErrBox>}
      {!detail && !detailErr && <div style={{ color: '#64748b' }}>Loading package…</div>}

      {detail && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 460px) 1fr', gap: 20, alignItems: 'start' }}>
          {/* Left: selection form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Pricing model */}
            <Panel title="Pricing model">
              <div style={{ display: 'flex', gap: 8 }}>
                <ModeBtn active={pricingMode === 'package'} onClick={() => { setPricingMode('package'); setResult(null) }}>Fixed package</ModeBtn>
                <ModeBtn active={pricingMode === 'byo'} onClick={() => { setPricingMode('byo'); setResult(null) }}>Build-your-own</ModeBtn>
              </div>
              {pricingMode === 'byo' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <div>
                    <label style={fieldLbl}>Reseller markup %</label>
                    <input type="number" min="0" max="100" step="0.5" value={resellerMarkupPct}
                      onChange={e => { setResellerMarkupPct(e.target.value); setResult(null) }} style={field} />
                  </div>
                  <div>
                    <label style={fieldLbl}>Payment mode</label>
                    <select value={paymentMode} onChange={e => { setPaymentMode(e.target.value); setResult(null) }} style={field}>
                      <option value="A">Mode A (InteGolf gateway)</option>
                      <option value="B">Mode B (reseller's own)</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: '#94a3b8' }}>
                    Price = hotel + golf (incl. our markup) + reseller markup + InteGolf fee + tax. Gateway (Mode A) is deducted from the reseller's margin.
                  </div>
                </div>
              )}
            </Panel>
            {/* Trip */}
            <Panel title="1 · Trip">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10 }}>
                <div><label style={fieldLbl}>Check-in</label><input type="date" value={checkIn} onChange={e => { setCheckIn(e.target.value); setHotelData(null); setHotelSel(null); setResult(null) }} style={field} /></div>
                <div><label style={fieldLbl}>Nights</label><input type="number" min="1" max="30" value={nights} onChange={e => { setNights(e.target.value); setHotelData(null); setResult(null) }} style={field} /></div>
                <div><label style={fieldLbl}>Golfers</label><input type="number" min="1" max="32" value={golfers} onChange={e => { setGolfers(e.target.value); setResult(null) }} style={field} /></div>
                <div><label style={fieldLbl}>Rooms</label><input type="number" min="1" max="16" value={rooms} onChange={e => { setRooms(e.target.value); setResult(null) }} style={field} /></div>
              </div>
              {checkOut && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>Check-out {fmtDate(checkOut)}</div>}
            </Panel>

            {/* Hotel */}
            <Panel title="2 · Hotel">
              {!checkIn ? <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Pick a check-in date first.</div> : (
                <>
                  <button onClick={findHotels} disabled={hotelLoading} style={{ ...pgBtn(hotelLoading), marginBottom: 10 }}>{hotelLoading ? 'Searching…' : (hotelData ? 'Re-search hotels' : 'Find hotels (live)')}</button>
                  {hotelErr && <ErrBox>{hotelErr}</ErrBox>}
                  {hotelSel && <div style={{ fontSize: 12.5, color: '#166534', marginBottom: 8 }}>✓ {hotelSel.name} — {hotelSel.roomType}</div>}
                  {hotelData && <HotelResults data={hotelData} nights={Number(nights) || 1} rooms={Number(rooms) || 1} selKey={hotelSel?.rateKey} onPick={(h, r) => setHotelSel({ rateKey: r.rateKey, costToken: r.costToken, name: h.name, roomType: r.roomType })} />}
                </>
              )}
            </Panel>

            {/* Rounds */}
            <Panel title="3 · Rounds">
              {rounds.map((r, i) => (
                <RoundRow key={i} idx={i} round={r} courses={courses} embed={embed} golfers={Number(golfers) || 1} packageId={sel.packageId} defaultDate={checkIn} onPatch={(patch) => patchRound(i, patch)} onRemove={rounds.length > 1 ? () => { setRounds(rs => rs.filter((_, j) => j !== i)); setResult(null) } : null} />
              ))}
              <button onClick={() => { setRounds(rs => [...rs, { coursesId: '', courseName: '', playDate: '', teeTime: '', ref: '' }]); setResult(null) }} style={{ ...pgBtn(false), marginTop: 4 }}>+ Add round</button>
            </Panel>

            <button onClick={runSim} disabled={!canRun || simLoading} style={{ ...primaryBtn, opacity: (!canRun || simLoading) ? 0.5 : 1, cursor: (!canRun || simLoading) ? 'default' : 'pointer' }}>
              {simLoading ? 'Computing…' : 'Compute breakdown →'}
            </button>
            {!canRun && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>Pick a check-in date, a hotel room, and a tee time for each round.</div>}
          </div>

          {/* Right: result */}
          <div>
            {simErr && <ErrBox>{simErr}</ErrBox>}
            {result ? <Breakdown detail={result} />
              : <div style={{ padding: 40, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc', color: '#64748b', fontSize: 13 }}>
                  Build a selection on the left, then Compute to see the live margin waterfall.
                </div>}
          </div>
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }) {
  return (
    <div style={{ border: '1px solid #e5eaf1', borderRadius: 10, padding: 14, background: '#fff' }}>
      <SectionTitle>{title}</SectionTitle>
      {children}
    </div>
  )
}

function HotelResults({ data, nights, rooms, selKey, onPick }) {
  const all = [
    ...(data.hotels || []).map(h => ({ h, over: false })),
    ...(data.upgrades || []).map(h => ({ h, over: true })),
  ]
  if (!all.length) return <div style={{ fontSize: 12.5, color: '#94a3b8' }}>No hotels for these dates. Try another check-in.</div>
  return (
    <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {all.map(({ h, over }) => (
        <div key={h.ref || h.propertyId || h.name} style={{ border: '1px solid #eef2f7', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: '#0f172a' }}>
            {h.name} {h.starRating ? <span style={{ color: '#f59e0b' }}>{'★'.repeat(Math.round(h.starRating))}</span> : null}
            {over && <span style={{ fontSize: 10.5, color: '#b45309', marginLeft: 6 }}>over budget</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
            {(h.rates || []).map(r => {
              const picked = selKey === r.rateKey
              const delta = over ? (r.upgradeNightly || 0) : (r.upgradeNightly || 0)
              return (
                <div key={r.rateKey} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '5px 7px', background: picked ? '#f0fdf4' : '#f8fafc', border: '1px solid ' + (picked ? '#16a34a' : 'transparent'), borderRadius: 4 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{r.roomType || 'Room'}{delta > 0 ? <span style={{ color: '#b45309' }}> · +{money(delta)}/night</span> : null}</span>
                  <button disabled={!r.costToken} onClick={() => onPick(h, r)} title={r.costToken ? '' : 'No live rate for this room'} style={{ ...pgBtn(!r.costToken), padding: '4px 10px', fontSize: 12, borderColor: picked ? '#16a34a' : '#cbd5e1', color: picked ? '#16a34a' : '#475569' }}>{picked ? '✓ Selected' : 'Select'}</button>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function RoundRow({ idx, round, courses, embed, golfers, packageId, defaultDate, onPatch, onRemove }) {
  const [times, setTimes] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const date = round.playDate || defaultDate

  function findTimes() {
    if (!embed || !round.coursesId || !date) return
    setLoading(true); setErr(null); setTimes(null)
    const params = new URLSearchParams({ date, golfers: String(golfers || 1) })
    if (packageId) params.set('packageId', String(packageId))
    embed.get(`/courses/${round.coursesId}/tee-times?${params.toString()}`).then(r => {
      if (r.status >= 400) setErr(r.data?.message || `Error ${r.status}`)
      else setTimes([...(r.data.slots || []), ...(r.data.upgrades || [])])
    }).catch(e => setErr(e.message)).finally(() => setLoading(false))
  }

  return (
    <div style={{ border: '1px solid #eef2f7', borderRadius: 8, padding: '9px 10px', marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b' }}>Round {idx + 1}</span>
        {onRemove && <button onClick={onRemove} style={{ border: 'none', background: 'transparent', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>remove</button>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 8, marginTop: 6 }}>
        <select value={round.coursesId} onChange={e => { const c = courses.find(x => String(x.coursesId) === e.target.value); onPatch({ coursesId: e.target.value, courseName: c?.courseName || '', teeTime: '', ref: '' }); setTimes(null) }} style={field}>
          <option value="">Select course…</option>
          {courses.map(c => <option key={c.coursesId} value={c.coursesId}>{c.courseName}</option>)}
        </select>
        <input type="date" value={date || ''} onChange={e => { onPatch({ playDate: e.target.value, teeTime: '', ref: '' }); setTimes(null) }} style={field} />
      </div>
      {round.coursesId && (
        <button onClick={findTimes} disabled={loading || !date} style={{ ...pgBtn(loading || !date), marginTop: 8, fontSize: 12, padding: '5px 10px' }}>{loading ? 'Searching…' : (times ? 'Re-search times' : 'Find tee times (live)')}</button>
      )}
      {err && <div style={{ fontSize: 11.5, color: '#991b1b', marginTop: 6 }}>{err}</div>}
      {times && (
        times.length === 0 ? <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>No tee times for that date.</div>
        : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {times.map(s => {
              const picked = round.teeTime === s.teeTime && round.ref === (s.ref || '')
              return (
                <button key={(s.ref || '') + s.teeTime} onClick={() => onPatch({ teeTime: s.teeTime, ref: s.ref || '' })} style={{ padding: '4px 9px', fontSize: 12, borderRadius: 999, cursor: 'pointer', border: '1px solid ' + (picked ? '#16a34a' : '#cbd5e1'), background: picked ? '#f0fdf4' : '#fff', color: picked ? '#166534' : '#475569', fontWeight: picked ? 700 : 500 }}>
                  {fmtTime(s.teeTime)}
                </button>
              )
            })}
          </div>
      )}
      {round.teeTime && <div style={{ fontSize: 11.5, color: '#166534', marginTop: 6 }}>✓ {round.courseName || 'Course'} · {fmtDate(date)} {fmtTime(round.teeTime)}</div>}
    </div>
  )
}
